/* eslint-disable react-dom/no-missing-iframe-sandbox */
import React from 'react'
import Analytics from 'analytics'
import googleTagManager from '@analytics/google-tag-manager'
import cookie from 'cookie'
import { filter } from 'lodash-es'

import Seo from './Seo'

import type { ISeoProps } from './Seo'

import RouteChangeProgress from '@/components/RouteChangeProgress'
import { useCanonicalHref, useLocale } from '@/hooks/routes'
import { TEST_ENV_VISITORS_NAME, useBaseCookies } from '@/hooks/baseCookies'
import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'
import { useNormalizePublicSrc } from '@/hooks/public'
import { ALL_LANGUAGES } from '@/components/LanguageSwitcher/data'
export interface BaseLayoutCoreProps {
  seo?: ISeoProps
  progressColor?: string
  showRouteProgress?: boolean
  locales?: string[] | null
}
export interface IBaseLayoutProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'id'> {
  seo?: ISeoProps
  progressColor?: string
  children: React.ReactNode
  showRouteProgress?: boolean
  minHScreen?: boolean
}
export const iconVersion = '?v2'
export interface BaseLayoutProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'id'>,
    BaseLayoutCoreProps {}

/**
 * 站群基础 layout 组件，内置 div#app-body 节点
 *
 * 由于站群项目在 Next.js 中无法按需引入全局样式，因此可将
 * div#app-body 节点理解为每个应用的虚拟 body 节点，方便做
 * 应用层面的全局样式写入和覆盖
 */
function Layout(props: BaseLayoutProps) {
  const {
    seo,
    progressColor,
    children,
    showRouteProgress = true,
    locales,
    ...divElementProps
  } = props

  const [canonicalHref] = useCanonicalHref()
  const { pathname } = useUrl()
  const locale = useLocale()
  const isHomePage = locale === 'en' && pathname === '/'
  const config = useConfig()
  const hostKey = useHostKey()
  const initializedRef = useRef(false)
  const { user } = useSiteSpecificContext()
  const normalizePublicSrc = useNormalizePublicSrc()
  const socialShareImg = `${normalizePublicSrc('/images/social-share.jpg')}`
  const reqInfo = user?.reqInfo || {}
  const { isInitialLoading: reqLoading } = reqInfo
  const [origin] = useOrigin()

  // 未传递 → 使用全部；传递空数组 → 空数组；传递部分 → 过滤对应项
  const localesList =
    locales === undefined || locales === null
      ? ALL_LANGUAGES
      : locales.length === 0
        ? []
        : filter(ALL_LANGUAGES, (item) => locales.includes(item.locale))

  const languageAlternates =
    localesList.length > 1
      ? [
          ...localesList.map((lang) => {
            return {
              hrefLang: lang.lang === 'pt-PT' ? 'pt-BR' : lang.lang,
              href: `${origin}${lang.abbr === 'en' ? '' : `/${lang.abbr}`}${pathname === '/' ? '' : pathname}`,
            }
          }),
          {
            hrefLang: 'x-default',
            href: `${origin}${pathname === '/' ? '' : pathname}`,
          },
        ]
      : []

  useEffect(() => {
    if (!config.analytics.gtmId || initializedRef.current || reqLoading) {
      return
    }
    const testEnvVisitors = cookie.parse(document.cookie)[
      TEST_ENV_VISITORS_NAME
    ]
    // 访问过测试环境的在正式环境不上报
    if (testEnvVisitors && process.env.NEXT_PUBLIC_APP_ENV === 'production') {
      return
    }
    const analytics = Analytics({
      app: hostKey,
      plugins: [
        googleTagManager({
          containerId: config.analytics.gtmId,
        }),
      ],
    })

    window.analytics = analytics
    initializedRef.current = true
  }, [config.analytics.gtmId, hostKey, reqLoading])
  useBaseCookies()

  return (
    <>
      <Seo
        languageAlternates={languageAlternates}
        canonical={isHomePage ? `${canonicalHref}/` : canonicalHref}
        {...seo}
        openGraph={{
          type: 'website',
          url: seo?.canonical || canonicalHref,
          title: seo?.title || '',
          description: seo?.description || '',
          siteName: config.name,
          images: [
            {
              url: seo?.openGraph?.images?.length
                ? seo.openGraph.images[0].url
                : socialShareImg,
              width: 1200,
              height: 630,
              alt: seo?.title || '',
            },
          ],
        }}
        additionalMetaTags={[
          {
            property: 'google', // 防止google翻译插件或者工具导致的报错 （https://github.com/facebook/react/issues/11538）
            content: 'notranslate',
          },
          ...(seo?.additionalMetaTags || []),
        ]}
        additionalLinkTags={[
          {
            rel: 'icon',
            // 各个站点独立的 icon 配置，
            // 据了解，部分爬虫可能是固定的 icon 路径
            href: `/icons/favicon.ico${iconVersion}`,
          },
          {
            rel: 'manifest', // https://developer.mozilla.org/zh-CN/docs/Web/Manifest
            crossOrigin: 'use-credentials', // https://web.dev/articles/add-manifest?hl=zh-cn
            href: '/manifest.json',
          },
          {
            rel: 'apple-touch-icon',
            href: `/icons/icon-180x180.png${iconVersion}`,
          },
          ...(seo?.additionalLinkTags || []),
        ]}
      />
      <div {...divElementProps} id='app-body'>
        {/* 通常都会有 GTM 配置，条件判断 config.gtmId 会导致 SSR 无该节点 */}
        <noscript>
          <iframe
            src={`https://www.googletagmanager.com/ns.html?id=${config.analytics.gtmId}`}
            height='0'
            width='0'
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
        {children}
      </div>
      {showRouteProgress && <RouteChangeProgress color={progressColor} />}
    </>
  )
}

export default Layout
